import { useCallback, useEffect, useRef } from 'react';
import Konva from 'konva';
import type { ViewportStage } from './useViewportStageController';

interface ActiveViewportPreview {
  base: ViewportStage;
  layers: Array<{
    layer: Konva.Layer;
    listening: boolean;
    canvas: HTMLCanvasElement;
    transform: string;
    transformOrigin: string;
    willChange: string;
  }>;
}

/**
 * Moves and scales the already-painted Konva canvases with a compositor
 * transform during viewport gestures. Updating Konva.Stage on every wheel
 * event redraws every scene and hit layer, which is prohibitively expensive
 * for dense charts.
 */
export function useCompositedStagePan(
  stageRef: { current: Konva.Stage | null },
): {
  previewCompositedPan: (next: ViewportStage) => void;
  settleCompositedPan: (next: ViewportStage) => void;
  cancelCompositedPan: () => void;
} {
  const activePreviewRef = useRef<ActiveViewportPreview | null>(null);
  const settleFrameRef = useRef<number | null>(null);

  const restorePreviewStyles = useCallback(() => {
    if (settleFrameRef.current !== null) {
      cancelAnimationFrame(settleFrameRef.current);
      settleFrameRef.current = null;
    }
    const active = activePreviewRef.current;
    if (!active) return;
    const previousAutoDraw = Konva.autoDrawEnabled;
    Konva.autoDrawEnabled = false;
    try {
      active.layers.forEach((entry) => {
        entry.layer.listening(entry.listening);
        entry.canvas.style.transform = entry.transform;
        entry.canvas.style.transformOrigin = entry.transformOrigin;
        entry.canvas.style.willChange = entry.willChange;
      });
    } finally {
      Konva.autoDrawEnabled = previousAutoDraw;
    }
    activePreviewRef.current = null;
  }, []);

  const previewCompositedPan = useCallback((next: ViewportStage) => {
    const stage = stageRef.current;
    if (!stage) return;
    if (settleFrameRef.current !== null) {
      cancelAnimationFrame(settleFrameRef.current);
      settleFrameRef.current = null;
    }
    let active = activePreviewRef.current;
    if (!active) {
      restorePreviewStyles();
      active = {
        base: {
          scale: stage.scaleX(),
          offsetX: stage.x(),
          offsetY: stage.y(),
        },
        layers: stage.getLayers().map((layer) => {
          const canvas = layer.getNativeCanvasElement();
          return {
            layer,
            listening: layer.listening(),
            canvas,
            transform: canvas.style.transform,
            transformOrigin: canvas.style.transformOrigin,
            willChange: canvas.style.willChange,
          };
        }),
      };
      activePreviewRef.current = active;
      const previousAutoDraw = Konva.autoDrawEnabled;
      Konva.autoDrawEnabled = false;
      try {
        active.layers.forEach((entry) => {
          entry.layer.listening(false);
          entry.canvas.style.transformOrigin = '0 0';
          entry.canvas.style.willChange = 'transform';
        });
      } finally {
        Konva.autoDrawEnabled = previousAutoDraw;
      }
    }

    const scaleRatio = next.scale / active.base.scale;
    const deltaX = next.offsetX - scaleRatio * active.base.offsetX;
    const deltaY = next.offsetY - scaleRatio * active.base.offsetY;
    active.layers.forEach((entry) => {
      const priorTransform = entry.transform ? `${entry.transform} ` : '';
      const scaleTransform = scaleRatio === 1 ? '' : ` scale(${scaleRatio})`;
      entry.canvas.style.transform = `${priorTransform}translate3d(${deltaX}px, ${deltaY}px, 0)${scaleTransform}`;
    });
  }, [restorePreviewStyles, stageRef]);

  const settleCompositedPan = useCallback((next: ViewportStage) => {
    const active = activePreviewRef.current;
    if (!active) return;
    const stage = stageRef.current;
    if (!stage) {
      restorePreviewStyles();
      return;
    }

    // Commit the logical transform, then let Konva coalesce the repaint with
    // react-konva's Stage prop update. A synchronous stage.draw() here painted
    // dense charts once immediately and again during the following React
    // commit, producing two consecutive long frames after every gesture.
    const previousAutoDraw = Konva.autoDrawEnabled;
    Konva.autoDrawEnabled = false;
    try {
      stage.scale({ x: next.scale, y: next.scale });
      stage.position({ x: next.offsetX, y: next.offsetY });
      active.layers.forEach((entry) => entry.layer.listening(entry.listening));
    } finally {
      Konva.autoDrawEnabled = previousAutoDraw;
    }
    stage.batchDraw();
    settleFrameRef.current = requestAnimationFrame(() => {
      settleFrameRef.current = null;
      restorePreviewStyles();
    });
  }, [restorePreviewStyles, stageRef]);

  useEffect(() => () => {
    if (settleFrameRef.current !== null) {
      cancelAnimationFrame(settleFrameRef.current);
      settleFrameRef.current = null;
    }
    restorePreviewStyles();
  }, [restorePreviewStyles]);

  return {
    previewCompositedPan,
    settleCompositedPan,
    cancelCompositedPan: restorePreviewStyles,
  };
}
